import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  describeGuessTarget,
  filterLabels,
  GUESS_IDS,
  guessHistoryLine,
  guessTask,
  idList,
  initialRace,
  lookupLabels,
  managerNames,
  MISSING_ID,
  PAGE_SIZE,
  pagingEventLabels,
  pagingMethodLabels,
  pagingOrderLabels,
  pagingReturned,
  pagingRun,
  people,
  queryFilter,
  raceCanStep,
  raceCode,
  raceRows,
  raceStatus,
  raceStepLabel,
  raceTone,
  raceTransition,
  sqlValue,
  viewers,
  type FilterPlace,
  type GuessId,
  type GuessResult,
  type Lookup,
  type PagesLoaded,
  type PagingEvent,
  type PagingMethod,
  type PagingOrder,
  type PagingPage,
  type RaceAction,
  type Viewer,
} from './query-models';

import './relationship-labs.css';
import './query-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  empty = 'No rows',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
  empty?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">{empty}</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const SIMULATION = 'A simulation of queries on the lesson dataset.';

export function QueryFilterLab() {
  const [viewer, setViewer] = useState<Viewer>('ravi');
  const [place, setPlace] = useState<FilterPlace>('python');
  const result = queryFilter(viewer, place);
  return (
    <Lab
      title="Which rows leave the database?"
      eyebrow="Protected queries · filter in SQL"
      className="rel-lab query-lab"
    >
      <Predict>
        Ravi opens his task page. Predict how many rows PostgreSQL sends with each filter, and what
        a route that forgets the Python filter shows.
      </Predict>
      <div className="cb-controls">
        <label>
          Viewer
          <ModelSelect
            aria-label="Viewer"
            value={viewer}
            onValueChange={(value) => setViewer(value as Viewer)}
            options={viewers.map((entry) => ({ value: entry, label: people[entry].name }))}
          />
        </label>
        <label>
          Filter
          <ModelSelect
            aria-label="Filter"
            value={place}
            onValueChange={(value) => setPlace(value as FilterPlace)}
            options={(Object.keys(filterLabels) as FilterPlace[]).map((key) => ({
              value: key,
              label: filterLabels[key],
            }))}
          />
        </label>
        <Reset
          onClick={() => {
            setViewer('ravi');
            setPlace('python');
          }}
        />
      </div>
      <Panel label="Query and filter">
        <pre aria-label="Query and filter">{result.code}</pre>
      </Panel>
      <Facts
        values={[
          ['Rows sent by PostgreSQL', `${result.sent.length}: ${idList(result.sent)}`],
          ['Rows the page shows', `${result.shown.length}: ${idList(result.shown)}`],
          ['A route that forgets the filter', result.forgetLine],
        ]}
      />
      <Table
        label="Rows sent by PostgreSQL"
        columns={['id', 'title', 'project_id', 'owner_id', 'On the page']}
        template="minmax(0, 0.4fr) minmax(0, 1.4fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 1.5fr)"
        className="query-wider query-ids"
        rows={result.sent.map((task) => [
          task.id,
          task.title,
          sqlValue(task.projectId),
          sqlValue(task.ownerId),
          result.onPage(task),
        ])}
      />
      <Result tone={result.tone}>{result.message}</Result>
      <p className="rel-caption">
        {SIMULATION} The tasks table here has six rows. With thousands, filtering in Python sends
        every row on every page load, and each route has to remember the filter.
      </p>
    </Lab>
  );
}

const MAX_HISTORY = 6;
export function IdGuessingLab() {
  const [lookup, setLookup] = useState<Lookup>('id');
  const [id, setId] = useState<GuessId>(9);
  const [history, setHistory] = useState<GuessResult[]>([]);
  const last = history.at(-1);
  const missing = guessTask(lookup, MISSING_ID);
  return (
    <Lab
      title="Can Jo read task 9 by typing its ID?"
      eyebrow="Protected queries · one row"
      className="rel-lab query-lab"
    >
      <Predict>
        Jo is in no project. Pick a lookup and an ID, and predict the status Jo gets before he sends
        the request.
      </Predict>
      <div className="cb-controls">
        <label>
          Lookup
          <ModelSelect
            aria-label="Lookup"
            value={lookup}
            onValueChange={(value) => setLookup(value as Lookup)}
            options={(Object.keys(lookupLabels) as Lookup[]).map((key) => ({
              value: key,
              label: lookupLabels[key],
            }))}
          />
        </label>
        <label>
          Requested ID
          <ModelSelect
            aria-label="Requested ID"
            value={String(id)}
            onValueChange={(value) => setId(Number(value) as GuessId)}
            options={GUESS_IDS.map((entry) => ({ value: String(entry), label: String(entry) }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          onClick={() =>
            setHistory((current) => [...current, guessTask(lookup, id)].slice(-MAX_HISTORY))
          }
        >
          Jo sends GET /tasks/{id}
        </Button>
        <Reset
          onClick={() => {
            setLookup('id');
            setId(9);
            setHistory([]);
          }}
        />
      </div>
      <Panel label="Query the route runs">
        <pre aria-label="Query the route runs">{guessTask(lookup, id).query}</pre>
      </Panel>
      <Facts
        values={[
          [`Task ${id}`, describeGuessTarget(id)],
          ['PostgreSQL returned', last ? last.returned : '(nothing sent yet)'],
          [`Task ${MISSING_ID}, which does not exist`, missing.answer],
        ]}
      />
      <div className="cb-two">
        <Panel label={last ? `Request · ${lookupLabels[last.lookup]}` : 'Request'}>
          <pre aria-label="Request">{last ? last.request.join('\n') : '(nothing sent yet)'}</pre>
        </Panel>
        <Panel label="Response">
          <pre aria-label="Response">{last ? last.response.join('\n') : '(no response yet)'}</pre>
        </Panel>
      </div>
      <Panel label="Requests so far">
        <pre aria-label="Requests so far">
          {history.length ? history.map(guessHistoryLine).join('\n') : '(none yet)'}
        </pre>
      </Panel>
      <Result tone={last?.tone ?? 'neutral'}>
        {last ? last.message : 'Choose a lookup and an ID, then send the request as Jo.'}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Jo&apos;s session cookie identifies him as user 3. A hidden task and a missing
        one get the same 404, so the answer tells Jo nothing about tasks he may not see.
      </p>
    </Lab>
  );
}

export function LastManagerRaceLab() {
  const [state, setState] = useState(() => initialRace());
  const act = (action: RaceAction) => setState((current) => raceTransition(current, action));
  return (
    <Lab
      title="Can two managers both step down?"
      eyebrow="Protected queries · the last manager"
      className="rel-lab query-lab"
    >
      <Predict>
        Maya and Ravi both manage Apollo and step down at the same moment. Demote both, count in
        both, then commit both: how many managers does Apollo keep? Then lock the project row and
        run the same order.
      </Predict>
      <div className="cb-controls">
        <label className="cb-check">
          <input type="checkbox" checked={state.lock} onChange={() => act('toggle-lock')} />
          Lock the project row
        </label>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={!raceCanStep(state, 'A')} onClick={() => act('step-a')}>
          {raceStepLabel(state, 'A')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!raceCanStep(state, 'B')}
          onClick={() => act('step-b')}
        >
          {raceStepLabel(state, 'B')}
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Panel label="Each transaction runs">
        <pre aria-label="Each transaction runs">{raceCode(state.lock)}</pre>
      </Panel>
      <Facts
        values={[
          ['A · Maya steps down', raceStatus(state, 'A')],
          ['B · Ravi steps down', raceStatus(state, 'B')],
          ['Managers of Apollo (committed)', managerNames(state.committed)],
        ]}
      />
      <Table
        label="Timeline"
        columns={['#', 'Step', 'What it saw']}
        template="minmax(0, 0.25fr) minmax(0, 1fr) minmax(0, 1.8fr)"
        className="query-wide"
        empty="No steps yet"
        rows={state.timeline.map((entry, index) => [index + 1, entry.step, entry.saw])}
      />
      <Table
        label="Apollo memberships"
        columns={['user_id', 'Committed role', 'A sees', 'B sees']}
        className="query-rows"
        rows={raceRows(state)}
      />
      <Result tone={raceTone(state)}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} Before this run, Maya made Ravi a manager of Apollo. Each transaction is READ
        COMMITTED, PostgreSQL&apos;s default: every statement sees committed rows plus its own
        changes.
      </p>
    </Lab>
  );
}

function PagePanel({ page, number }: { page: PagingPage | null; number: 1 | 2 }) {
  return (
    <Panel label={`Page ${number}`}>
      {page ? (
        <>
          <pre aria-label={`Page ${number} query`}>{page.query}</pre>
          <Table
            label={`Page ${number} rows`}
            columns={['id', 'title', 'Returned']}
            className="query-ids"
            rows={page.fetched.map((task) => [task.id, task.title, pagingReturned(page, task)])}
          />
          <Facts values={[[`After page ${number}`, page.next]]} />
        </>
      ) : (
        <p className="rel-caption">Not loaded yet</p>
      )}
    </Panel>
  );
}

export function KeysetPagingLab() {
  const [method, setMethod] = useState<PagingMethod>('offset');
  const [order, setOrder] = useState<PagingOrder>('oldest');
  const [event, setEvent] = useState<PagingEvent>('none');
  const [loaded, setLoaded] = useState<PagesLoaded>(0);
  const run = pagingRun(method, order, event, loaded);
  const change =
    <T,>(set: (value: T) => void) =>
    (value: string) => {
      set(value as T);
      setLoaded(0);
    };
  return (
    <Lab
      title="Does the next page skip or repeat a task?"
      eyebrow="Protected queries · keyset paging"
      className="rel-lab query-lab"
    >
      <Predict>
        Load page 1, then page 2, with task 9 deleted in between. Which task does OFFSET never show?
        Then list newest first and add task 15 between the pages: which task appears twice?
      </Predict>
      <div className="cb-controls">
        <label>
          Method
          <ModelSelect
            aria-label="Method"
            value={method}
            onValueChange={change(setMethod)}
            options={(Object.keys(pagingMethodLabels) as PagingMethod[]).map((key) => ({
              value: key,
              label: pagingMethodLabels[key],
            }))}
          />
        </label>
        <label>
          Order
          <ModelSelect
            aria-label="Order"
            value={order}
            onValueChange={change(setOrder)}
            options={(Object.keys(pagingOrderLabels) as PagingOrder[]).map((key) => ({
              value: key,
              label: pagingOrderLabels[key],
            }))}
          />
        </label>
        <label>
          Between the pages
          <ModelSelect
            aria-label="Between the pages"
            value={event}
            onValueChange={change(setEvent)}
            options={(Object.keys(pagingEventLabels) as PagingEvent[]).map((key) => ({
              value: key,
              label: pagingEventLabels[key],
            }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setLoaded(1)}>
          Load page 1
        </Button>
        <Button size="sm" variant="secondary" disabled={loaded !== 1} onClick={() => setLoaded(2)}>
          Load page 2
        </Button>
        <Reset
          onClick={() => {
            setMethod('offset');
            setOrder('oldest');
            setEvent('none');
            setLoaded(0);
          }}
        />
      </div>
      <Facts
        values={[
          ['Page size', String(PAGE_SIZE)],
          ['Between the pages', run.between],
        ]}
      />
      <div className="cb-two">
        <PagePanel page={run.page1} number={1} />
        <PagePanel page={run.page2} number={2} />
      </div>
      <Table
        label="Apollo's tasks"
        columns={['id', 'title', 'Shown on']}
        className="query-ids"
        rows={run.rows}
      />
      <Result tone={run.tone}>{run.message}</Result>
      <p className="rel-caption">
        {SIMULATION} IDs only grow, so a new task lands at the end of an oldest-first list and at
        the start of a newest-first one. The API you build lists oldest first: WHERE t.id &gt; after
        ORDER BY t.id, fetching one row more than the page size.
      </p>
    </Lab>
  );
}
